import { useState, useEffect, useCallback } from 'react';
import { userRequest } from '../requestMethods';

// containerVersion is used to force a re-render of the container when the version changes (e.g. when the translation is toggled).
// It was causing the mapping to be lost when the translation was toggled, hence when a word is clicked it doesn't work.
const useInteractiveText = (formattedText, textId, uniqueClassName, startCount = 0, containerVersion = 0) => {
    const [clickedWord, setClickedWord] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [highlightedSpans, setHighlightedSpans] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [rateLimitError, setRateLimitError] = useState(null);

    const open = Boolean(anchorEl);

    // Generate a random key for mapping span indexes
    const generateRandomKey = () => {
        return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    };

    const resetHighlights = useCallback(() => {
        highlightedSpans.forEach(span => {
            if (span) {
                span.style.textDecoration = 'none';
            }
        });
        setHighlightedSpans([]);
    }, [highlightedSpans]);



    const handleClose = useCallback(() => {
        resetHighlights();
        setAnchorEl(null);
        setClickedWord(null);
    }, [resetHighlights]);

    // Direct API call function using stable identity
    const handleWordClickDirect = useCallback(async (e, spanIndex) => {
        resetHighlights();
        const clickedSpan = e.target;
        
        if (spanIndex !== -1 && textId) {
            setIsLoading(true);
            try {
                const response = await userRequest.post(`/text/word/${textId}`, {
                    seg: startCount + spanIndex
                });
                
                const wordData = response.data;
                
                // Get all spans for highlighting within the specific container
                const container = document.querySelector(`.${uniqueClassName}`);
                const spans = container ? container.querySelectorAll('.chp') : [];
                
                // Highlight spans based on returned indexes (si = spanIndexes)
                const spansToHighlight = wordData.si.map(idx => {
                    const span = spans[idx];
                    if (span) {
                        span.style.textDecoration = 'underline';
                    }
                    return span;
                });
                
                setHighlightedSpans(spansToHighlight);
                setClickedWord(wordData);
                setAnchorEl(clickedSpan);
            } catch (error) {
                console.error('Error fetching word data:', error);
                
                // Handle rate limiting errors
                if (error.response && error.response.status === 429) {
                    const errorData = error.response.data;
                    setRateLimitError(errorData);
                }
            } finally {
                setIsLoading(false);
            }
        }
    }, [resetHighlights, textId, startCount, uniqueClassName]);

    // Set up event delegation with single click listener for performance
    // Rebind whenever the container might be re-rendered (e.g., layout toggles)
    useEffect(() => {
        if (!formattedText || !textId) return;

        const timer = setTimeout(() => {
            // Find the container that holds all the spans (or use body as fallback)
            const container = document.querySelector(`.${uniqueClassName}`) || document.body;
            
            if (!container) return;

            // Create new mapping for this text setup
            const newSpanIndexMap = new Map();
            
            // Add random keys to spans and build the mapping
            const spans = container.querySelectorAll('.chp');
            spans.forEach((span, index) => {
                const randomKey = generateRandomKey();
                newSpanIndexMap.set(randomKey, index);
                span.setAttribute('data', randomKey);
            });
            
            // Log security enhancement info
            console.log(`🔒 Interactive Text Security Enhancement:`, {
                spanCount: spans.length,
                mappingSize: newSpanIndexMap.size,
                sampleMappings: Array.from(newSpanIndexMap.entries()).slice(0, 3).map(([key, index]) => `${key} -> ${index}`),
                securityNote: 'Using random keys instead of predictable indexes',
                textId: textId
            });

            // Single event listener using event delegation
            const delegatedClickHandler = (e) => {
                // Check if clicked element is a span with class 'chp'
                if (e.target.classList.contains('chp')) {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    // Get the random key from data attribute
                    const spanKey = e.target.getAttribute('data');
                    
                    // Look up the actual index using the mapping
                    if (spanKey && newSpanIndexMap.has(spanKey)) {
                        const spanIndex = newSpanIndexMap.get(spanKey);
                        handleWordClickDirect(e, spanIndex);
                    }
                }
            };

            // Add single listener to container
            container.addEventListener('click', delegatedClickHandler);
            

            // Cleanup function
            return () => {
                container.removeEventListener('click', delegatedClickHandler);
                // Clean up data attributes and cursor styles
                spans.forEach(span => {
                    span.removeAttribute('data');
                });
            };
        }, 100);

        return () => {
            clearTimeout(timer);
        };
    }, [formattedText, textId, uniqueClassName, containerVersion, handleWordClickDirect]);

    

    return {
        clickedWord,
        anchorEl,
        open,
        handleClose,
        resetHighlights,
        isLoading,
        rateLimitError,
        setRateLimitError
    };
};

export default useInteractiveText; 